import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { applications as seedApplications, auditSeed, CURRENT_REP_ID, defaultFlags, disputes as seedDisputes, earnings as seedEarnings, fieldTasks as seedTasks, fraudFlags as seedFlags, incidents as seedIncidents, intelReports as seedIntel, leads as seedLeads, networkCampuses as seedCampuses, performanceEvents as seedEvents, referrals as seedReferrals, representatives as seedReps, rewardRules as seedRules, trainingModules, verifications as seedVerifications } from '../data/network';
import { evidenceFlags, id, metresBetween, nextRank, ruleAmount } from '../utils/network';
import type { ApplicationStatus, DocumentReviewState, AuditEntry, CapturedEvidence, Dispute, EarningEntry, FeatureFlagKey, FeatureFlags, FieldTask, FraudFlag, Incident, IntelCategory, IntelReport, Lead, NetworkCampus, PerformanceEvent, Referral, RepApplication, Representative, RepRank, RepStatus, RewardRule, VerificationDecision, VerificationSubmission } from '../types/network';
const TODAY = '14 Aug 2026';
interface ApprovalPayload {
  campusId: string;
  areaId: string | null;
  rank: RepRank;
  supervisorRepId: string | null;
  reason: string;
}
interface NetworkValue {
  campuses: NetworkCampus[];
  reps: Representative[];
  currentRep: Representative;
  applications: RepApplication[];
  tasks: FieldTask[];
  verifications: VerificationSubmission[];
  earnings: EarningEntry[];
  rules: RewardRule[];
  referrals: Referral[];
  leads: Lead[];
  intel: IntelReport[];
  events: PerformanceEvent[];
  incidents: Incident[];
  fraud: FraudFlag[];
  disputes: Dispute[];
  audit: AuditEntry[];
  flags: FeatureFlags;

  /* applicant → representative */
  submitApplication: (draft: Omit<RepApplication, 'id' | 'status' | 'submittedOn' | 'internalNotes'>) => string;
  reviewApplicationDocument: (applicationId: string, documentKey: string, state: DocumentReviewState, note: string | null, actor: string) => void;
  moveApplication: (applicationId: string, status: ApplicationStatus, reason: string) => void;
  approveApplication: (applicationId: string, payload: ApprovalPayload) => void;
  addApplicationNote: (applicationId: string, body: string) => void;

  /* onboarding and training */
  completeOnboardingStep: (repId: string, key: string) => void;
  completeTraining: (repId: string, moduleId: string) => void;

  /* field work */
  acceptTask: (taskId: string, repId: string) => void;
  startTask: (taskId: string) => void;
  submitVerification: (taskId: string, repId: string, captured: CapturedEvidence, offline: boolean) => {
    submissionId: string;
    flags: string[];
  };
  syncSubmission: (submissionId: string) => void;
  reviewVerification: (submissionId: string, decision: VerificationDecision, notes: string, reviewer: string) => void;

  /* finance */
  approveEarning: (earningId: string, actor: string) => void;
  rejectEarning: (earningId: string, actor: string, reason: string) => void;
  reverseEarning: (earningId: string, actor: string, reason: string) => void;
  payoutApproved: (repId: string, actor: string) => void;

  /* growth */
  addLead: (lead: Omit<Lead, 'id' | 'createdOn'>) => void;
  advanceLead: (leadId: string) => void;
  addIntel: (report: {
    campusId: string;
    areaId: string | null;
    category: IntelCategory;
    observation: string;
    confidence: IntelReport['confidence'];
    expiresOn: string | null;
    repId: string;
  }) => void;
  reviewIntel: (reportId: string, status: IntelReport['status'], actor: string) => void;

  /* governance */
  reportIncident: (incident: Omit<Incident, 'id' | 'reportedOn' | 'status' | 'handledBy'>) => void;
  resolveFraudFlag: (flagId: string, state: 'Cleared' | 'Upheld', actor: string) => void;
  raiseDispute: (dispute: Omit<Dispute, 'id' | 'raisedOn' | 'state' | 'decision'>) => void;
  decideDispute: (disputeId: string, state: 'Upheld' | 'Declined', decision: string, actor: string) => void;
  setRepStatus: (repId: string, status: RepStatus, reason: string, actor: string) => void;
  promote: (repId: string, actor: string, reason: string) => void;
  updateRule: (ruleId: string, patch: Partial<RewardRule>, actor: string) => void;
  createCampus: (campus: Omit<NetworkCampus, 'id'>) => void;
  publishCampus: (campusId: string, actor: string) => void;
  toggleFlag: (key: FeatureFlagKey, actor: string) => void;
}
const NetworkContext = createContext<NetworkValue | null>(null);
export function NetworkProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [campuses, setCampuses] = useState(seedCampuses);
  const [reps, setReps] = useState(seedReps);
  const [applications, setApplications] = useState(seedApplications);
  const [tasks, setTasks] = useState(seedTasks);
  const [verifications, setVerifications] = useState(seedVerifications);
  const [earnings, setEarnings] = useState(seedEarnings);
  const [rules, setRules] = useState(seedRules);
  const [referrals, setReferrals] = useState(seedReferrals);
  const [leads, setLeads] = useState(seedLeads);
  const [intel, setIntel] = useState(seedIntel);
  const [events, setEvents] = useState(seedEvents);
  const [incidents, setIncidents] = useState(seedIncidents);
  const [fraud, setFraud] = useState(seedFlags);
  const [disputes, setDisputes] = useState(seedDisputes);
  const [audit, setAudit] = useState(auditSeed);
  const [flags, setFlags] = useState(defaultFlags);

  /** Every consequential action lands here. Records are appended, never edited. */
  const log = useCallback((entry: Omit<AuditEntry, 'id' | 'at'>) => {
    setAudit(current => [{
      ...entry,
      id: id('AUD'),
      at: `${TODAY}, ${new Date().toLocaleTimeString('en-NG', {
        hour: '2-digit',
        minute: '2-digit'
      })}`
    }, ...current]);
  }, []);
  const currentRep = reps.find(rep => rep.id === CURRENT_REP_ID) ?? reps[0];

  /* ------------------------------------------------------- applications -- */

  const submitApplication = useCallback<NetworkValue['submitApplication']>(draft => {
    const applicationId = id('APP');
    setApplications(current => [{
      ...draft,
      id: applicationId,
      status: 'Submitted',
      submittedOn: TODAY,
      internalNotes: []
    }, ...current]);
    log({
      actor: draft.fullName,
      action: 'Representative application submitted',
      entity: applicationId,
      from: 'Draft',
      to: 'Submitted',
      reason: null
    });
    return applicationId;
  }, [log]);

  /**
   * A document has its own review lifecycle, separate from the application's.
   * An application cannot be approved while a required document is unverified.
   */
  const reviewApplicationDocument = useCallback<NetworkValue['reviewApplicationDocument']>((applicationId, documentKey, state, note, actor) => {
    setApplications(current => current.map(application => {
      if (application.id !== applicationId) return application;
      return {
        ...application,
        documents: application.documents.map(document => document.key === documentKey ? {
          ...document,
          state,
          reviewNote: note
        } : document)
      };
    }));
    log({
      actor,
      action: `Application document ${state.toLowerCase()}`,
      entity: `${applicationId} · ${documentKey}`,
      from: null,
      to: state,
      reason: note
    });
  }, [log]);
  const moveApplication = useCallback<NetworkValue['moveApplication']>((applicationId, status, reason) => {
    setApplications(current => current.map(application => {
      if (application.id !== applicationId) return application;
      log({
        actor: 'Ops — K. Yusuf',
        action: 'Application status changed',
        entity: applicationId,
        from: application.status,
        to: status,
        reason: reason || null
      });
      return {
        ...application,
        status,
        decisionReason: reason || application.decisionReason
      };
    }));
  }, [log]);
  const approveApplication = useCallback<NetworkValue['approveApplication']>((applicationId, payload) => {
    setApplications(current => current.map(application => application.id === applicationId ? {
      ...application,
      status: 'Approved',
      assignedCampusId: payload.campusId,
      assignedAreaId: payload.areaId ?? undefined,
      assignedRank: payload.rank,
      supervisorRepId: payload.supervisorRepId ?? undefined,
      decisionReason: payload.reason
    } : application));
    const application = applications.find(item => item.id === applicationId);
    if (!application) return;
    const repId = id('rep');
    const campus = campuses.find(item => item.id === payload.campusId);
    setReps(current => [...current, {
      id: repId,
      repCode: `CR-${(campus?.institution ?? 'CMP').slice(0, 6).toUpperCase()}-${current.length + 1}`,
      name: application.fullName,
      initials: application.fullName.split(' ').slice(0, 2).map(part => part[0]).join(''),
      campusId: payload.campusId,
      areaId: payload.areaId,
      rank: payload.rank,
      status: 'Pending onboarding',
      supervisorRepId: payload.supervisorRepId,
      joinedOn: TODAY,
      referralCode: `CZ-${application.fullName.split(' ')[0].toUpperCase()}`,
      onboarding: [{
        key: 'terms',
        label: 'Representative agreement',
        detail: 'Scope of authority and conduct.',
        required: true,
        done: false
      }, {
        key: 'conduct',
        label: 'Code of conduct',
        detail: 'What you may and may not represent.',
        required: true,
        done: false
      }, {
        key: 'privacy',
        label: 'Privacy acknowledgement',
        detail: 'Handling landlord and student data.',
        required: true,
        done: false
      }, {
        key: 'identity',
        label: 'Identity confirmation',
        detail: 'Student ID checked by the campus lead.',
        required: true,
        done: false
      }, {
        key: 'payout',
        label: 'Payout details',
        detail: 'Held by the payment provider, not Campuzon.',
        required: false,
        done: false
      }, {
        key: 'training',
        label: 'Core training',
        detail: 'Orientation and evidence standards.',
        required: true,
        done: false
      }],
      trainingCompleted: [],
      warnings: 0,
      historic: {
        verified: 0,
        referred: 0,
        businesses: 0
      }
    }]);
    log({
      actor: 'Ops — K. Yusuf',
      action: 'Application approved and representative account created',
      entity: applicationId,
      from: application.status,
      to: `Approved · ${payload.rank} at ${campus?.name ?? payload.campusId}`,
      reason: payload.reason
    });
  }, [applications, campuses, log]);
  const addApplicationNote = useCallback<NetworkValue['addApplicationNote']>((applicationId, body) => {
    setApplications(current => current.map(application => application.id === applicationId ? {
      ...application,
      internalNotes: [...application.internalNotes, {
        at: TODAY,
        actor: 'Ops — K. Yusuf',
        body
      }]
    } : application));
  }, []);

  /* ------------------------------------------- onboarding and training -- */

  const completeOnboardingStep = useCallback<NetworkValue['completeOnboardingStep']>((repId, key) => {
    setReps(current => current.map(rep => {
      if (rep.id !== repId) return rep;
      const onboarding = rep.onboarding.map(step => step.key === key ? {
        ...step,
        done: true
      } : step);
      const ready = onboarding.filter(step => step.required).every(step => step.done);
      if (ready && rep.status === 'Pending onboarding') {
        log({
          actor: rep.name,
          action: 'Onboarding completed — account activated',
          entity: rep.repCode,
          from: 'Pending onboarding',
          to: 'Active',
          reason: null
        });
      }
      return {
        ...rep,
        onboarding,
        status: ready && rep.status === 'Pending onboarding' ? 'Active' : rep.status
      };
    }));
  }, [log]);
  const completeTraining = useCallback<NetworkValue['completeTraining']>((repId, moduleId) => {
    setReps(current => current.map(rep => rep.id === repId && !rep.trainingCompleted.includes(moduleId) ? {
      ...rep,
      trainingCompleted: [...rep.trainingCompleted, moduleId]
    } : rep));
  }, []);

  /* -------------------------------------------------------- field work -- */

  const acceptTask = useCallback<NetworkValue['acceptTask']>((taskId, repId) => {
    const rep = reps.find(item => item.id === repId);
    setTasks(current => current.map(task => task.id === taskId ? {
      ...task,
      status: 'Accepted',
      assignedRepId: repId,
      history: [...task.history, {
        at: `${TODAY}`,
        actor: rep?.name ?? repId,
        label: 'Accepted'
      }]
    } : task));
    log({
      actor: rep?.name ?? repId,
      action: 'Task accepted',
      entity: taskId,
      from: 'Open',
      to: 'Accepted',
      reason: null
    });
  }, [log, reps]);
  const startTask = useCallback<NetworkValue['startTask']>(taskId => {
    setTasks(current => current.map(task => task.id === taskId ? {
      ...task,
      status: 'In progress'
    } : task));
  }, []);
  const submitVerification = useCallback<NetworkValue['submitVerification']>((taskId, repId, captured, offline) => {
    const task = tasks.find(item => item.id === taskId);
    const rep = reps.find(item => item.id === repId);
    const distance = task?.expectedLocation && captured.gps ? metresBetween(task.expectedLocation, captured.gps) : null;
    const priorHashes = verifications.flatMap(submission => submission.evidence.photos.map(photo => photo.dataUrl));
    const detected = evidenceFlags(captured, distance, priorHashes);
    const submissionId = id('VER');
    setVerifications(current => [{
      id: submissionId,
      taskId,
      repId,
      propertyRef: task?.propertyRef ?? task?.title ?? taskId,
      submittedOn: TODAY,
      status: offline ? 'Submitted' : 'Under review',
      evidence: captured,
      distanceFromExpectedM: distance,
      flags: detected,
      syncState: offline ? 'Queued offline' : 'Synced'
    }, ...current]);
    setTasks(current => current.map(item => item.id === taskId ? {
      ...item,
      status: offline ? 'Submitted' : 'Under review',
      history: [...item.history, {
        at: TODAY,
        actor: rep?.name ?? repId,
        label: offline ? 'Submitted — queued offline' : 'Submitted for review'
      }]
    } : item));
    if (detected.length > 0) {
      setFraud(current => [...detected.filter(flag => flag.includes('radius') || flag.includes('matches evidence')).map(flag => ({
        id: id('FRD'),
        repId,
        signal: flag.includes('radius') ? 'Location inconsistency' : 'Duplicate evidence',
        detail: flag,
        severity: 'High' as const,
        raisedOn: TODAY,
        state: 'Awaiting review' as const
      })), ...current]);
    }
    log({
      actor: rep?.name ?? repId,
      action: 'Verification submitted',
      entity: submissionId,
      from: null,
      to: offline ? 'Queued offline' : 'Under review',
      reason: detected.length > 0 ? `${detected.length} review flag(s) raised` : null
    });
    return {
      submissionId,
      flags: detected
    };
  }, [log, reps, tasks, verifications]);
  const syncSubmission = useCallback<NetworkValue['syncSubmission']>(submissionId => {
    setVerifications(current => current.map(submission => submission.id === submissionId ? {
      ...submission,
      syncState: 'Synced',
      status: 'Under review'
    } : submission));
  }, []);
  const reviewVerification = useCallback<NetworkValue['reviewVerification']>((submissionId, decision, notes, reviewer) => {
    const submission = verifications.find(item => item.id === submissionId);
    if (!submission) return;
    const task = tasks.find(item => item.id === submission.taskId);
    setVerifications(current => current.map(item => item.id === submissionId ? {
      ...item,
      status: decision,
      reviewerId: reviewer,
      reviewNotes: notes,
      decidedOn: TODAY,
      verifiedUntil: decision === 'Approved' ? '14 Feb 2027' : undefined
    } : item));
    setTasks(current => current.map(item => item.id === submission.taskId ? {
      ...item,
      status: decision === 'Approved' ? 'Approved' : decision === 'Rejected' ? 'Rejected' : 'Rework required',
      reviewerId: reviewer,
      reviewNotes: notes,
      history: [...item.history, {
        at: TODAY,
        actor: reviewer,
        label: decision
      }]
    } : item));
    setEvents(current => [{
      id: id('PE'),
      repId: submission.repId,
      at: TODAY,
      kind: decision === 'Approved' ? 'Verification approved' : decision === 'Rejected' ? 'Verification rejected' : 'Verification reworked',
      weightNote: 'Accuracy',
      delta: decision === 'Approved' ? 2 : -3
    }, ...current]);

    /* An approval is what creates an earning — never the submission itself. */
    if (decision === 'Approved' && task) {
      const rule = rules.find(item => item.id === task.rewardRuleId);
      if (rule && rule.active) {
        setEarnings(current => [{
          id: id('ERN'),
          repId: submission.repId,
          ruleId: rule.id,
          activity: `${task.type} — ${task.title}`,
          sourceRef: task.id,
          grossAmount: ruleAmount(rule),
          adjustment: 0,
          currency: rule.currency,
          status: rule.requiresApproval ? 'Pending' : 'Approved',
          createdOn: TODAY,
          approvedOn: rule.requiresApproval ? null : TODAY,
          approvedBy: rule.requiresApproval ? null : 'Automatic — rule does not require approval',
          paidOn: null
        }, ...current]);
      }
    }
    log({
      actor: reviewer,
      action: `Verification ${decision.toLowerCase()}`,
      entity: submissionId,
      from: submission.status,
      to: decision,
      reason: notes || null
    });
  }, [log, rules, tasks, verifications]);

  /* ----------------------------------------------------------- finance -- */

  const approveEarning = useCallback<NetworkValue['approveEarning']>((earningId, actor) => {
    setEarnings(current => current.map(entry => entry.id === earningId ? {
      ...entry,
      status: 'Approved',
      approvedOn: TODAY,
      approvedBy: actor
    } : entry));
    log({
      actor,
      action: 'Earning approved',
      entity: earningId,
      from: 'Pending',
      to: 'Approved',
      reason: null
    });
  }, [log]);
  const rejectEarning = useCallback<NetworkValue['rejectEarning']>((earningId, actor, reason) => {
    setEarnings(current => current.map(entry => entry.id === earningId ? {
      ...entry,
      status: 'Rejected',
      reason
    } : entry));
    log({
      actor,
      action: 'Earning rejected',
      entity: earningId,
      from: 'Pending',
      to: 'Rejected',
      reason
    });
  }, [log]);
  const reverseEarning = useCallback<NetworkValue['reverseEarning']>((earningId, actor, reason) => {
    const original = earnings.find(entry => entry.id === earningId);
    if (!original) return;
    setEarnings(current => [{
      id: id('ERN'),
      repId: original.repId,
      ruleId: original.ruleId,
      activity: `Reversal of ${original.id}`,
      sourceRef: original.sourceRef,
      grossAmount: -original.grossAmount,
      adjustment: 0,
      currency: original.currency,
      status: 'Approved',
      createdOn: TODAY,
      approvedOn: TODAY,
      approvedBy: actor,
      paidOn: null,
      reversalOf: original.id,
      reason
    }, ...current.map(entry => entry.id === earningId ? {
      ...entry,
      status: 'Reversed' as const,
      reason
    } : entry)]);
    log({
      actor,
      action: 'Earning reversed by adjustment entry',
      entity: earningId,
      from: original.status,
      to: 'Reversed',
      reason
    });
  }, [earnings, log]);
  const payoutApproved = useCallback<NetworkValue['payoutApproved']>((repId, actor) => {
    setEarnings(current => current.map(entry => entry.repId === repId && entry.status === 'Approved' ? {
      ...entry,
      status: 'Paid',
      paidOn: TODAY
    } : entry));
    log({
      actor,
      action: 'Payout released',
      entity: repId,
      from: 'Approved',
      to: 'Paid',
      reason: 'Settlement window'
    });
  }, [log]);

  /* ------------------------------------------------------------ growth -- */

  const addLead = useCallback<NetworkValue['addLead']>(lead => {
    setLeads(current => [{
      ...lead,
      id: id('LEAD'),
      createdOn: TODAY
    }, ...current]);
  }, []);
  const advanceLead = useCallback<NetworkValue['advanceLead']>(leadId => {
    const order: Lead['stage'][] = ['Created', 'Contacted', 'Interested', 'Onboarding', 'Verified', 'Active'];
    setLeads(current => current.map(lead => {
      if (lead.id !== leadId) return lead;
      const index = order.indexOf(lead.stage);
      return index === -1 || index === order.length - 1 ? lead : {
        ...lead,
        stage: order[index + 1]
      };
    }));
  }, []);
  const addIntel = useCallback<NetworkValue['addIntel']>(report => {
    setIntel(current => [{
      ...report,
      id: id('INT'),
      observedOn: TODAY,
      status: 'Submitted',
      hasEvidence: false
    }, ...current]);
  }, []);
  const reviewIntel = useCallback<NetworkValue['reviewIntel']>((reportId, status, actor) => {
    setIntel(current => current.map(report => report.id === reportId ? {
      ...report,
      status
    } : report));
    log({
      actor,
      action: `Intelligence marked ${status.toLowerCase()}`,
      entity: reportId,
      from: null,
      to: status,
      reason: null
    });
  }, [log]);

  /* ------------------------------------------------------- governance -- */

  const reportIncident = useCallback<NetworkValue['reportIncident']>(incident => {
    const incidentId = id('INC');
    setIncidents(current => [{
      ...incident,
      id: incidentId,
      reportedOn: TODAY,
      status: 'Open',
      handledBy: null
    }, ...current]);
    log({
      actor: incident.confidential ? 'Confidential reporter' : incident.repId,
      action: 'Incident reported',
      entity: incidentId,
      from: null,
      to: 'Open',
      reason: incident.kind
    });
  }, [log]);
  const resolveFraudFlag = useCallback<NetworkValue['resolveFraudFlag']>((flagId, state, actor) => {
    setFraud(current => current.map(flag => flag.id === flagId ? {
      ...flag,
      state
    } : flag));
    log({
      actor,
      action: `Fraud flag ${state.toLowerCase()}`,
      entity: flagId,
      from: 'Awaiting review',
      to: state,
      reason: null
    });
  }, [log]);
  const raiseDispute = useCallback<NetworkValue['raiseDispute']>(dispute => {
    setDisputes(current => [{
      ...dispute,
      id: id('DSP'),
      raisedOn: TODAY,
      state: 'Open',
      decision: null
    }, ...current]);
  }, []);
  const decideDispute = useCallback<NetworkValue['decideDispute']>((disputeId, state, decision, actor) => {
    setDisputes(current => current.map(dispute => dispute.id === disputeId ? {
      ...dispute,
      state,
      decision
    } : dispute));
    log({
      actor,
      action: `Dispute ${state.toLowerCase()}`,
      entity: disputeId,
      from: 'Under review',
      to: state,
      reason: decision
    });
  }, [log]);
  const setRepStatus = useCallback<NetworkValue['setRepStatus']>((repId, status, reason, actor) => {
    setReps(current => current.map(rep => {
      if (rep.id !== repId) return rep;
      log({
        actor,
        action: 'Representative status changed',
        entity: rep.repCode,
        from: rep.status,
        to: status,
        reason
      });
      return {
        ...rep,
        status
      };
    }));
  }, [log]);
  const promote = useCallback<NetworkValue['promote']>((repId, actor, reason) => {
    setReps(current => current.map(rep => {
      if (rep.id !== repId) return rep;
      const target = nextRank(rep.rank);
      if (!target) return rep;
      log({
        actor,
        action: 'Representative promoted',
        entity: rep.repCode,
        from: rep.rank,
        to: target,
        reason
      });
      return {
        ...rep,
        rank: target
      };
    }));
  }, [log]);
  const updateRule = useCallback<NetworkValue['updateRule']>((ruleId, patch, actor) => {
    setRules(current => current.map(rule => {
      if (rule.id !== ruleId) return rule;
      log({
        actor,
        action: 'Reward rule changed',
        entity: rule.label,
        from: JSON.stringify(Object.fromEntries(Object.keys(patch).map(key => [key, (rule as never)[key as never]]))),
        to: JSON.stringify(patch),
        reason: 'Configuration change'
      });
      return {
        ...rule,
        ...patch
      };
    }));
  }, [log]);
  const createCampus = useCallback<NetworkValue['createCampus']>(campus => {
    const campusId = id('cmp');
    setCampuses(current => [...current, {
      ...campus,
      id: campusId
    }]);
    log({
      actor: 'Ops — K. Yusuf',
      action: 'Campus created',
      entity: campus.name,
      from: null,
      to: 'Unpublished',
      reason: null
    });
  }, [log]);
  const publishCampus = useCallback<NetworkValue['publishCampus']>((campusId, actor) => {
    setCampuses(current => current.map(campus => campus.id === campusId ? {
      ...campus,
      published: true,
      active: true,
      recruitmentStatus: 'Recruiting' as const
    } : campus));
    log({
      actor,
      action: 'Campus published',
      entity: campusId,
      from: 'Unpublished',
      to: 'Published',
      reason: null
    });
  }, [log]);
  const toggleFlag = useCallback<NetworkValue['toggleFlag']>((key, actor) => {
    setFlags(current => {
      const next = !current[key];
      log({
        actor,
        action: 'Feature flag changed',
        entity: key,
        from: String(current[key]),
        to: String(next),
        reason: 'Controlled rollout'
      });
      return {
        ...current,
        [key]: next
      };
    });
  }, [log]);
  const value = useMemo<NetworkValue>(() => ({
    reviewApplicationDocument,
    campuses,
    reps,
    currentRep,
    applications,
    tasks,
    verifications,
    earnings,
    rules,
    referrals,
    leads,
    intel,
    events,
    incidents,
    fraud,
    disputes,
    audit,
    flags,
    submitApplication,
    moveApplication,
    approveApplication,
    addApplicationNote,
    completeOnboardingStep,
    completeTraining,
    acceptTask,
    startTask,
    submitVerification,
    syncSubmission,
    reviewVerification,
    approveEarning,
    rejectEarning,
    reverseEarning,
    payoutApproved,
    addLead,
    advanceLead,
    addIntel,
    reviewIntel,
    reportIncident,
    resolveFraudFlag,
    raiseDispute,
    decideDispute,
    setRepStatus,
    promote,
    updateRule,
    createCampus,
    publishCampus,
    toggleFlag
  }), [reviewApplicationDocument, campuses, reps, currentRep, applications, tasks, verifications, earnings, rules, referrals, leads, intel, events, incidents, fraud, disputes, audit, flags, submitApplication, moveApplication, approveApplication, addApplicationNote, completeOnboardingStep, completeTraining, acceptTask, startTask, submitVerification, syncSubmission, reviewVerification, approveEarning, rejectEarning, reverseEarning, payoutApproved, addLead, advanceLead, addIntel, reviewIntel, reportIncident, resolveFraudFlag, raiseDispute, decideDispute, setRepStatus, promote, updateRule, createCampus, publishCampus, toggleFlag]);
  return <NetworkContext.Provider value={value} data-id="element-5391" data-mp="2632v026i5q8c">{children}</NetworkContext.Provider>;
}
export function useNetwork(): NetworkValue {
  const context = useContext(NetworkContext);
  if (!context) throw new Error('useNetwork must be used inside NetworkProvider');
  return context;
}
export { trainingModules };